import React, { useCallback, useEffect, useState } from 'react';
import { DriverFilters } from '../Filter/DriverFilters';
import { GET_ALL_DRIVERS_MASTER_DATA } from '../../graphql/client/queries';
import {
    GetAllDriversMasterData,
    GetAllDriversMasterData_getAllDriversMasterData as Driver,
} from '../../types/schemaTypes';
import { Error } from '../_common';
import { useStoreContext } from '../App/StoreProvider';
import { useMutation } from '@apollo/client/react';
import { Query } from '../_common';
import './Drivers.scss';
import { SearchTypeEnum } from '../../types/SearchTypeEnum';
import SearchBar from '../SearchBar/SearchBar';
import DriverList from './DriverList';
import { useAuthContext } from '../Auth/AuthProvider';
import { PermissionEnum } from '../../types/PermissionEnum';
import { ModalForm, Option } from './interface';
import AddDriverModal from './AddEditDriverModal/AddEditDriverModal';
import { SaveDriver } from '../../types/schemaTypes';
import { SAVE_DRIVER, DELETE_DRIVER } from '../../graphql/client/mutations';

import {  ToastContainer, toast } from 'react-toastify';
import {toastOpt} from '../../utils/helpers/toastHelper'
import { Alert } from 'react-bootstrap';
import * as LoadManagementListActions from '../../store/actions/loadManagementList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Do not change this value unless the associate backend value has been updated - (8/22/19) BS
const PAGE_SIZE = 50;
const toasterOptions = { duration: 2500 };
const Messages = {
    delete: 'Driver deleted successfully',
};
const ErrorMessages = {
    delete: 'Failed to delete Driver',
};

let selectedDriver: Driver | undefined;

const Drivers = (): React.ReactElement => {
    const [showAddDriverModal, setShowAddDriverModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [modalForm, setModalForm] = useState(ModalForm.EMPTY);
    document.title = 'SBS';
    const [listHeight, setListHeight] = useState(window.screen.height);
    const listWidth = '1450px';
    const [state, dispatch] = useStoreContext();
    const {
        loadManagementList: { filters, searchPhrase },
    } = state;
    const abortController = new AbortController();
    useEffect(() => {
        dispatch(LoadManagementListActions.resetFilterState());
    }, []);
    const canEditSBXDrivers = useAuthContext().userHasPermission(PermissionEnum.EditDriver);
    const canEditThirdPartyDrivers = useAuthContext().userHasPermission(PermissionEnum.Edit3rdPartyCarrier);
    const canEditDrivers = canEditSBXDrivers || canEditThirdPartyDrivers;

    const [showFilter, setShowFilter] = useState(false);

    const variables = {
        input: {
            regionIds: filters.regionIds,
            districtIds: filters.districtIds,
            statuses: filters.statuses,
            carrierIds: filters.carrierIds,
            search: searchPhrase,
            activeStatus: filters.activeStatus,
            compliantStatus: filters.compliantStatus,
            isAvailable: filters.isAvailable,
            checkInWithBellyDump: filters.checkInWithBellyDump,
        },
    };

    const context = {
        fetchOptions: {
            signal: abortController.signal,
        },
    };

    const abortRequest = (): void => {
        abortController.abort();
    };

    const renderToast = (message, type = 'info') => {
        interface ToastProps {
            onClose?: () => void;
        }
        const Comp = (props: ToastProps) => {
            return (
                <Alert variant={type} onClose={props.onClose}>
                    {message}
                </Alert>
            );
        };

        Comp.displayName = 'toast';
        return Comp;
    };

    const onCompleted = action => {
        toast.success(Messages[action],toastOpt);
    };
    const onActionError = action => {
        toast.error(ErrorMessages[action],toastOpt);
    };

    const [deleteDriver] = useMutation(DELETE_DRIVER, {
        onCompleted: () => onCompleted('delete'),
        onError: () => onActionError('delete'),
        refetchQueries: () => {
            return [
                {
                    query: GET_ALL_DRIVERS_MASTER_DATA,
                    variables: {
                        input: {
                            regionIds: filters.regionIds,
                            districtIds: filters.districtIds,
                            statuses: filters.statuses,
                            carrierIds: filters.carrierIds,
                            search: searchPhrase,
                            activeStatus: filters.activeStatus,
                            compliantStatus: filters.compliantStatus,
                            isAvailable: filters.isAvailable,
                            checkInWithBellyDump: filters.checkInWithBellyDump,
                        },
                    },
                },
            ];
        },
    });

    const onDelete = useCallback(async (record: any) => {
        const variables = { input: { id: record.driverId } };
        await deleteDriver({ variables });
    }, []);

    const handleOnHide = (): void => {
        selectedDriver = undefined;
        setErrorMessage('');
        setShowAddDriverModal(false);
    };

    const onCompletedDriver = (data: SaveDriver): void => {
        if (data.saveDriver === null) return;
        const saveDriverErrors = data.saveDriver.errors;
        if (saveDriverErrors && saveDriverErrors.length > 0) {
            setErrorMessage(saveDriverErrors[0] || '');
        } else {
            selectedDriver = undefined;
            setErrorMessage('');
            setShowAddDriverModal(false);
        }
    };

    const [saveDriver] = useMutation<SaveDriver>(SAVE_DRIVER, {
        onCompleted: onCompletedDriver,
        refetchQueries: () => {
            return [
                {
                    query: GET_ALL_DRIVERS_MASTER_DATA,
                    variables: {
                        input: {
                            regionIds: filters.regionIds,
                            districtIds: filters.districtIds,
                            statuses: filters.statuses,
                            carrierIds: filters.carrierIds,
                            search: searchPhrase,
                            activeStatus: filters.activeStatus,
                            compliantStatus: filters.compliantStatus,
                            isAvailable: filters.isAvailable,
                            checkInWithBellyDump: filters.checkInWithBellyDump,
                        },
                    },
                },
            ];
        },
    });

    const handleOnSave = async (
        id: number,
        driverName: string,
        cellPhone: string,
        cdlNumber: string,
        cdlExpiryDate: Date,
        kronosId: string,
        isActive: boolean,
        isAssigned: boolean,
        isManual: boolean,
        isAvailable: boolean,
        isShiftStarted: boolean,
        isLocked: boolean,
        driverCompanyId: number,
        extSystemName: string,
        extSystemIdentifier: string,
        activeInactiveDate: Date,
        inactiveReason: string,
        reactivationDate: Date,
        regionIds: string,
        districtIds: string,
        extSystemIdentifier3: string,
    ): Promise<void> => {
        // if (selectedDriver) {
        //     selectedDriver.driverId = id;
        //     selectedDriver.name = driverName;
        //     selectedDriver.cellPhone = cellPhone;
        //     selectedDriver.cdlNumber = cdlNumber;
        //     selectedDriver.cdlExpiryDate = cdlExpiryDate.toString();
        //     selectedDriver.kronosId = kronosId;
        //     selectedDriver.isActive = isActive;
        //     selectedDriver.isAssigned = isAssigned;
        //     selectedDriver.isManual = isManual;
        //     selectedDriver.isAvailable = isAvailable;
        //     selectedDriver.isShiftStarted = isShiftStarted;
        //     selectedDriver.isLocked = isLocked;
        //     selectedDriver.driverCompanyId = driverCompanyId;
        //     selectedDriver.extSystemName = extSystemName;
        //     selectedDriver.extSystemIdentifier = extSystemIdentifier;
        //     selectedDriver.activeInactiveDate = activeInactiveDate ? activeInactiveDate.toString() : '';
        //     selectedDriver.inactiveReason = inactiveReason;
        //     selectedDriver.reactivationDate = reactivationDate ? reactivationDate.toString() : '';
        //     selectedDriver.regionIds = regionIds;
        //     selectedDriver.districtIds = districtIds;
        //     selectedDriver.extSystemIdentifier3 = extSystemIdentifier3;
        // }

        const variables = {
            input: {
                id,
                driverName,
                cellPhone,
                cdlNumber,
                cdlExpiryDate,
                kronosId,
                isActive,
                isAssigned,
                isManual,
                isAvailable,
                isShiftStarted,
                isLocked,
                driverCompanyId,
                extSystemName,
                extSystemIdentifier,
                activeInactiveDate,
                inactiveReason,
                reactivationDate,
                regionIds,
                districtIds,
                extSystemIdentifier3,
            },
        };
        await saveDriver({ variables });
    };

    const options: Option[] = [];

    const setListContainerHeight = (): void => {
        const filterDiv = document.getElementById('loadsFilterContainer');
        const listDiv = document.getElementById('loadsListContainer');

        let filterHeight = 0;
        // setTimeout in place to avoid animation miscalculations
        setTimeout(() => {
            if (filterDiv) {
                filterHeight = filterDiv.scrollHeight;
            }
            if (listDiv) {
                setListHeight(Math.min(filterHeight));
            }
        }, 100);
    };
    document.title = 'Drivers';
    return (
        <Query<GetAllDriversMasterData> query={GET_ALL_DRIVERS_MASTER_DATA} variables={variables} fetchPolicy="network-only">
            {({
                error,
                data,
                loading,
                refetch,
                fetchMore,
            }): React.ReactElement | null => {
                if (error) {
                    return <Error error={error} />;
                }

                const driverList: Driver[] = [];

                if (data && data.getAllDriversMasterData && data.getAllDriversMasterData.length !== 0) {
                    data.getAllDriversMasterData.forEach((element: any | null): void => {
                        if (element !== null) {
                            driverList.push(element);
                        }
                    });
                }
                const offset =
                    data && data.getAllDriversMasterData ? data.getAllDriversMasterData.length / PAGE_SIZE : 0;

                let isEndOfResults = false;
                if (
                    offset % 1 !== 0 ||
                    (!isEndOfResults &&
                        data &&
                        data.getAllDriversMasterData &&
                        data.getAllDriversMasterData.length < PAGE_SIZE)
                ) {
                    // If offset is not a whole number, the end of the results has been reached. (5/11/19) - KS
                    isEndOfResults = true;
                }

                return (
                    <div className="JobLoadsManagement-Container">
                        <ToastContainer />
                        <div className="Header-Container">
                            <div className="Header-SearchBar">
                                <div
                                    className="Filter-HeaderIconContainer JobLoads-Filter"
                                    onClick={() => setShowFilter(!showFilter)}
                                    style={{ position: 'relative' }}
                                >
                                    <i className="material-icons">filter_list</i>
                                    {((filters.regionIds && filters.regionIds.length > 0) ||
                                        (filters.districtIds && filters.districtIds.length > 0) ||
                                        (filters.statuses && filters.statuses.length > 0) ||
                                        (filters.carrierIds && filters.carrierIds.length > 0) ||
                                        (filters.activeStatus && filters.activeStatus.length > 0) ||
                                        (filters.compliantStatus && filters.compliantStatus.length > 0) ||
                                        (filters.isAvailable && filters.isAvailable.length > 0) ||
                                        (filters.checkInWithBellyDump && filters.checkInWithBellyDump.length > 0)) && (
                                            <FontAwesomeIcon
                                                icon={['fas', 'check-circle']}
                                                color="#fb6e3a"
                                                size="sm"
                                                style={{ position: 'absolute', top: '5px', right: '-4px' }}
                                            />
                                        )}

                                </div>
                                <SearchBar type={SearchTypeEnum.LoadManagement}></SearchBar>
                            </div>
                            <div className="Drivers-RightOptions">
                                <div></div>
                                {canEditDrivers && (
                                    <div
                                        className="Drivers-Create-New-Driver Row"
                                        onClick={() => {
                                            selectedDriver = undefined;
                                            setShowAddDriverModal(true);
                                            setModalForm(ModalForm.ADD_DRIVER);
                                        }}
                                    >
                                        <i className="material-icons Users-Icon">add_circle</i>
                                        <span className="Users-Link">Create New Driver</span>
                                    </div>
                                )}
                            </div>
                            <div className="Header-ListHeader"></div>
                        </div>
                        <div className="JobLoadsManagement-BodyContainer">
                            <DriverFilters abortRequest={abortRequest} onFilterToggle={setListContainerHeight} showFilter={showFilter} setShowFilter={setShowFilter} />
                            <div
                                id="loadsListContainer"
                                className="Drivers-ListContainer"
                                style={{ height: listHeight, width: listWidth }}
                            >
                                {/* {JSON.stringify(data)} */}
                                <DriverList
                                    onDelete={onDelete}
                                    abortRequest={abortRequest}
                                    driverList={driverList}
                                    isLoading={loading}
                                    isEndOfResults={isEndOfResults}
                                    canEditDrivers={canEditThirdPartyDrivers}
                                    canEditSBXDrivers={canEditSBXDrivers}
                                    onEditClick={Driver => {
                                        selectedDriver = Driver;
                                        setShowAddDriverModal(true);
                                        setModalForm(ModalForm.EDIT_DRIVER);
                                    }}
                                    onLoadMore={() =>
                                        fetchMore({
                                            variables: {
                                                offset,
                                            },
                                            updateQuery: (prev: any, { fetchMoreResult }: any) => {
                                                if (
                                                    prev &&
                                                    fetchMoreResult &&
                                                    prev.getAllDriversMasterData &&
                                                    fetchMoreResult.getAllDriversMasterData
                                                ) {
                                                    return Object.assign({}, prev, {
                                                        getAllDriversMasterData: [
                                                            ...prev.getAllDriversMasterData,
                                                            ...fetchMoreResult.getAllDriversMasterData,
                                                        ],
                                                    });
                                                } else {
                                                    return prev;
                                                }
                                            },
                                        })
                                    }
                                />

                                <AddDriverModal
                                    show={showAddDriverModal}
                                    modalForm={modalForm}
                                    options={options}
                                    driverId={selectedDriver?.driverId ?? 0}
                                    onClickHide={handleOnHide}
                                    onClickSave={handleOnSave}
                                    errorMessage={errorMessage}
                                ></AddDriverModal>
                            </div>
                        </div>
                    </div>
                );
            }}
        </Query>
    );
};

export default Drivers;
